import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, Teleport as _Teleport, createVNode as _createVNode, createElementVNode as _createElementVNode, createElementBlock as _createElementBlock, withCtx as _withCtx } from "vue"

const _hoisted_1 = {
  key: 1,
  class: "button-row"
}

import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useGeneralStore } from '@/pinia'
import TableBase from '@/design-system/atoms/TableBase.vue'
import TextBase from '@/design-system/atoms/TextBase.vue'
import BadgeSquare from '@/design-system/atoms/BadgeSquare.vue'
import SvgDisplay from '@/design-system/atoms/SvgDisplay.vue'
import useFetchProjects, { ProjectQuery } from '@/api-client/models/project/useFetchProjects'
import { PROJECT_STATUS, PROJECT_TYPE, BADGE_COLORS } from '@/api-client/models/project/Constants'
import CardBase from '@/components/AnalyticsDashboard/components/CardBase.vue'
import ProjectDetails from '@/components/AnalyticsDashboard/components/ProjectDetails.vue'
import { Table } from '@/design-system/atoms/table-base'
import { FeatureType, useCurrentFeatureFilters, useFilters } from '@/composables/useFilters'
import { PROJECT_STATUSES, Project, ProjectStatus } from '@/api-client/models/project/Contracts'
import { calculateEntityIds, calculateEntityType } from '@/fetches'

type HeaderCell = {
  label: {
    title: string
    subtitle?: string
  }
}

type BodyCell = {
  cell: {
    index: number
    label: string
    id?: string
    status?: ProjectStatus
    badgeColor?: string
    backgroundColor?: string
    project: Project
  }
}

type ProjectTable = Table<HeaderCell, BodyCell>


export default /*@__PURE__*/_defineComponent({
  __name: 'ProjectListTable',
  setup(__props) {

const piniaStore = useGeneralStore()

const hoveredIndex = ref<number | null>(null)
const showDetails = ref(false)
const selectedProject = ref<Project | undefined>(undefined)
const selectedBadgeColor = ref<string | undefined>(undefined)
const { locale } = useI18n()
const query = ref<ProjectQuery>()

const { data } = useFetchProjects(query)

const currentFeature = useCurrentFeatureFilters()
const filterType = computed(
  (): FeatureType.ProjectAnalysisExternal | FeatureType.ProjectAnalysisInternal => {
    if (!currentFeature.value) {
      return FeatureType.ProjectAnalysisExternal
    }
    return currentFeature.value.submitted.type as
      | FeatureType.ProjectAnalysisExternal
      | FeatureType.ProjectAnalysisInternal
  }
)
const isFeatureFullscreen = computed(() => {
  return piniaStore.isFeatureFullscreen
})
const filters = useFilters<
  FeatureType.ProjectAnalysisExternal | FeatureType.ProjectAnalysisInternal
>(filterType.value)

watch(
  () => filters.submitted,
  (value) => {
    if (!value) {
      query.value = {
        page: 0,
        pageSize: 100,
        internal: filters.submitted?.type === FeatureType.ProjectAnalysisInternal
      }
      return
    }

    const internal = value.type === FeatureType.ProjectAnalysisInternal
    const entityType = calculateEntityType(value)
    const entityIds = calculateEntityIds(value)

    const budgetFrom = value.budget?.from ? value.budget.from * 1000 : undefined
    const budgetTo = value.budget?.to ? value.budget.to * 1000 : undefined

    query.value = {
      page: 0,
      pageSize: 100,
      internal,
      budgetFrom,
      budgetTo,
      entityType,
      entityIds,
      statuses: value.projectStatuses,
      types: value.projectTypes
    }
  },
  { immediate: true }
)

const filteredItems = computed(() => {
  return data.value?.items ?? []
})

const table = computed<ProjectTable>((): ProjectTable => {
  const headers: ProjectTable['headers'] = {
    top: [
      {
        label: {
          title: `${filteredItems.value.length} Projekt`
        },
        style: {
          width: '40%',
          height: '32px',
          padding: '4px',
          'background-color': '#D5D7F666'
        }
      },
      {
        label: {
          title: 'Typ'
        },
        style: {
          width: '35%',
          height: '32px',
          padding: '4px',
          'text-align': 'left',
          'background-color': '#D5D7F666'
        }
      },
      {
        label: {
          title: 'Status'
        },
        style: {
          width: '25%',
          height: '32px',
          padding: '4px',
          'text-align': 'left',
          'background-color': '#D5D7F666'
        }
      }
    ]
  }
  const body: ProjectTable['body'] = filteredItems.value.map((project, index) => {
    return [
      {
        cell: {
          index: index,
          label: project.name
        },
        style: {
          width: '40%',
          padding: '4px',
          backgroundColor: hoveredIndex.value === index ? '#CDDEFE33' : 'transparent',
          cursor: 'pointer'
        }
      },
      {
        cell: {
          index: index,
          label: PROJECT_TYPE[project.type][locale.value]
        },
        style: {
          width: '35%',
          padding: '4px',
          backgroundColor: hoveredIndex.value === index ? '#CDDEFE33' : 'transparent',
          cursor: 'pointer'
        }
      },
      {
        cell: {
          index: index,
          id: project.id,
          label: PROJECT_STATUS[project.status][locale.value],
          status: project.status,
          badgeColor: BADGE_COLORS[project.status],
          project: project
        },
        style: {
          width: '25%',
          padding: '4px',
          backgroundColor: hoveredIndex.value === index ? '#CDDEFE33' : 'transparent',
          cursor: 'pointer'
        }
      }
    ]
  })
  return {
    headers,
    body
  }
})

function onClickRow(row?: BodyCell[]) {
  if (!row || !row?.[2].cell.project || !row?.[2].cell.badgeColor) {
    return
  }
  selectedBadgeColor.value = row?.[2].cell.badgeColor
  selectedProject.value = row?.[2].cell.project
  showDetails.value = true
}

function statusOrder(status?: ProjectStatus) {
  return status ? PROJECT_STATUSES.indexOf(status) : 0
}

return (_ctx: any,_cache: any) => {
  return (table.value)
    ? (_openBlock(), _createBlock(CardBase, { key: 0 }, {
        default: _withCtx(() => [
          (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
            (showDetails.value && selectedProject.value && selectedBadgeColor.value)
              ? (_openBlock(), _createBlock(ProjectDetails, {
                  key: 0,
                  project: selectedProject.value,
                  internal: _unref(filters).submitted.type === _unref(FeatureType).ProjectAnalysisInternal,
                  selectedBadgeColor: selectedBadgeColor.value,
                  onProjectDetailsClose: _cache[0] || (_cache[0] = () => (showDetails.value = false))
                }, null, 8, ["project", "internal", "selectedBadgeColor"]))
              : _createCommentVNode("", true)
          ])),
          _createVNode(TableBase, {
            table: table.value,
            bodyHeight: isFeatureFullscreen.value ? 'auto' : '300px',
            "sort-by": (i) => statusOrder(table.value.body[i][2].cell.status),
            layout: "fixed",
            rightTextAlign: "center",
            tabbableRows: true,
            caption: "Projekt table",
            onMouseEnterCell: _cache[1] || (_cache[1] = ($event: any) => (hoveredIndex.value = $event.cell.index)),
            onMouseLeaveCell: _cache[2] || (_cache[2] = ($event: any) => (hoveredIndex.value = null)),
            onOnClickRow: onClickRow
          }, {
            "header-cell": _withCtx(({ cell: { label } }) => [
              _createVNode(TextBase, {
                text: label?.title,
                textWrap: "nowrap",
                type: 'paragraph',
                weight: 'bold'
              }, null, 8, ["text"])
            ]),
            "body-cell": _withCtx(({ cell, cellIndex }) => [
              _createElementVNode("div", null, [
                (cellIndex % 3 === 0)
                  ? (_openBlock(), _createBlock(TextBase, {
                      key: 0,
                      text: cell.cell.label,
                      textWrap: "nowrap",
                      type: 'paragraph',
                      weight: 'regular',
                      overflow: 'ellipsis',
                      "overflow-tooltip": ""
                    }, null, 8, ["text"]))
                  : _createCommentVNode("", true)
              ]),
              (cellIndex % 3 === 1)
                ? (_openBlock(), _createBlock(BadgeSquare, {
                    key: 0,
                    badgeText: cell.cell.label,
                    size: 'small',
                    color: 'primary',
                    style: { textAlign: 'left' }
                  }, null, 8, ["badgeText"]))
                : _createCommentVNode("", true),
              (cellIndex % 3 === 2)
                ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
                    _createVNode(BadgeSquare, {
                      badgeText: cell.cell.label,
                      size: 'small',
                      containerStyle: { 'background-color': cell.cell.badgeColor }
                    }, null, 8, ["badgeText", "containerStyle"]),
                    _createVNode(SvgDisplay, { src: 'Icon-Arrow-Up-Right_x16' })
                  ]))
                : _createCommentVNode("", true)
            ]),
            _: 1
          }, 8, ["table", "bodyHeight", "sort-by"])
        ]),
        _: 1
      }))
    : _createCommentVNode("", true)
}
}

})